import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BankDetailsForm } from '../../../Components/AccountPage/BankDetails/BankDetailsForm';
import { Button } from '@Components/common';
import styles from './BankDetailsController.module.scss';
import { BankDetails } from '@Components/AccountPage/BankDetails/BankDetails';
import { useGetDueDiligenceDetails, useUpdateDueDiligenceDetails } from '@Hooks/Api';
import { useNotificationsContext, useUserContext } from '@Contexts';
import { Loader } from '@Components';
import { setBackendFieldErrors } from '@Helpers/setBackendFieldErrors';

export const BankDetailsController = ({ afterSubmit }: { afterSubmit?: () => void }) => {
  const { t } = useTranslation('account');
  const methods = useForm();
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const { setError } = methods;
  const [getDueDiligenceData, { data: dueDiligenceData, loading: dueDiligenceDataLoading }] =
    useGetDueDiligenceDetails();
  const [
    updateDueDiligenceData,
    { loading: isUpdateDueDiligenceDataLoading, error: UpdateDueDiligenceDataError },
  ] = useUpdateDueDiligenceDetails();
  const { userData } = useUserContext();

  const organizationId = userData?.organization_id;
  const [accountNumber, setAccountNumber] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [accountName, setAccountName] = useState('');

  useEffect(() => {
    if (organizationId) {
      getDueDiligenceData(organizationId).then(({ data }) => {
        setAccountNumber(data.bank_details.account_number);
        setSortCode(data.bank_details.sort_code);
        setAccountName(data.bank_details.account_name);
      });
    }
  }, [organizationId, getDueDiligenceData, setAccountNumber, setSortCode, setAccountName]);

  const hasBankDetails = useMemo(() => {
    return !!accountNumber && !!sortCode && !!accountName;
  }, [accountNumber, sortCode, accountName]);

  const [displayBankDetailsForm, setDisplayBankDetailsForm] = useState(false);

  const onSubmit = methods.handleSubmit(
    (data) => {
      if (organizationId) {
        updateDueDiligenceData(organizationId, { bank_details: data })
          .then((response) => {
            successNotification(
              t('update_success_message', { section: t('tabs.bank_details.title') }),
            );
            setDisplayBankDetailsForm(false);
            setAccountNumber(response.data.bank_details.account_number);
            setSortCode(response.data.bank_details.sort_code);
            setAccountName(response.data.bank_details.account_name);
            afterSubmit && afterSubmit();
          })
          .catch(({ data }) => {
            if (data?.field_errors) {
              setBackendFieldErrors(data.field_errors, setError);
            } else {
              errorNotification(t('common:errors.generic_error'));
            }
          });
      }
    },
    () => {},
  );

  if (dueDiligenceDataLoading) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className={styles.formContainer}>
          {displayBankDetailsForm || !hasBankDetails ? (
            <>
              <BankDetailsForm />
              <div className={styles.buttonContainer}>
                {hasBankDetails && (
                  <Button
                    buttonType="tertiary"
                    className={styles.button}
                    onClick={() => setDisplayBankDetailsForm(false)}
                  >
                    {t('back')}
                  </Button>
                )}
                <Button
                  buttonType={'primary'}
                  className={styles.button}
                  type="submit"
                  disabled={isUpdateDueDiligenceDataLoading}
                  loading={isUpdateDueDiligenceDataLoading}
                >
                  {t('save')}
                </Button>
              </div>
            </>
          ) : (
            <BankDetails
              accountNumber={!!accountNumber ? accountNumber : ''}
              sortCode={!!sortCode ? sortCode : ''}
              accountName={!!accountName ? accountName : ''}
              setDisplayBankDetailsForm={setDisplayBankDetailsForm}
              onSave={afterSubmit}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};
